import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSession, useUser, useClerk } from '@clerk/react';
import axiosInstance from '../utils/axiosInstance';
import { setClerkSession } from '../utils/axiosInstance';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { session } = useSession();
  const { isSignedIn, isLoaded: clerkLoaded, user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const loggingOut = useRef(false);

  const fetchUserDetails = useCallback(async (activeSession) => {
    try {
      const token = await activeSession.getToken();
      if (!token) return;
      const response = await axiosInstance.get('api/users/me/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawName = clerkUser?.firstName || response.data.first_name || response.data.username;
      const displayName = rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : '';
      const userData = {
        ...response.data,
        displayName,
        clerkImageUrl: clerkUser?.imageUrl || null,
      };
      localStorage.setItem('userDetails', JSON.stringify(userData));
      setUser(userData);
    } catch (e) {
      console.error('Error fetching user details:', e);
    }
  }, [clerkUser]);

  const logout = useCallback(async (sendMessage = true) => {
    loggingOut.current = true;
    setLoading(true);
    localStorage.removeItem('userDetails');
    setUser(null);

    if (sendMessage) {
      const authChannel = new BroadcastChannel('auth');
      authChannel.postMessage({ type: 'logout' });
    }

    try {
      await signOut();
    } catch (e) {
      console.error('Error signing out of Clerk:', e);
    }
  }, [signOut]);

  // Set session for axios interceptor (for all other API calls after login)
  useEffect(() => {
    if (session) {
      setClerkSession(session);
    } else {
      setClerkSession(null);
    }
    return () => setClerkSession(null);
  }, [session]);

  // Fetch user details when signed in with active session
  useEffect(() => {
    if (!clerkLoaded) return;

    if (isSignedIn && session && !user && !loggingOut.current) {
      fetchUserDetails(session).finally(() => setLoading(false));
    } else if (!isSignedIn && !session) {
      loggingOut.current = false;
      setUser(null);
      setLoading(false);
    }
  }, [clerkLoaded, isSignedIn, session, user, fetchUserDetails]);

  // Cross-tab sync
  useEffect(() => {
    const authChannel = new BroadcastChannel('auth');
    authChannel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        logout(false);
      } else if (event.data.type === 'login' && session) {
        fetchUserDetails(session);
      }
    };
    return () => authChannel.close();
  }, [logout, fetchUserDetails, session]);

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
